// src/encounter/intelligence/acknowledgement-gate.tsx
//
// The AI notice gate in front of the Scrips Intelligence cards (DoH §3.4 AI-literacy acknowledgement). Contract read
// from scrips-clinician-demo at 13df5c0, scripts/foundry/scripsServer.ts `acknowledgement()` and
// src/engine/acknowledgements.ts:
//   GET  /api/intelligence/acknowledgement -> 200 { version 'ai-literacy/3', text, lines[], confirm, listed, acknowledged: {version, date, name} | null }
//   POST /api/intelligence/acknowledgement { confirmed: true, name, version } -> 201 { acknowledged }
//        400 bad name / body, 403 not listed, 409 note version changed, 503 store down.
// Rules: the notice lines and the confirm label are rendered verbatim (the app writes no notice text); the cards show
// only once `acknowledged` is set for the current version; a person not on the named list sees nothing; a malformed
// reply fails closed (no cards). Same auth as the Explain card (the clinician's own token). Nothing logs. The name is
// typed by the person, never pre-filled.
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button, TextField } from '@scripsteam/scrips-signal-ds';
import { authStorage } from '@/auth/storage';
import { C } from './explain-card-classes';
import { N } from './next-step-classes';
import { SESSION_EXPIRED, STATE_LINE } from './explanation';
import { ACK_LINE, ACK_PATH, looksLikeFullName, parseAck, parseAckNotice, type AckNotice, type FetchLike } from './acknowledgement';

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const ackOf = (body: unknown) => (isObj(body) ? parseAck(body.acknowledged) : undefined);

export interface AcknowledgementGateProps {
  children: ReactNode;
  fetch?: FetchLike;
  token?: () => string | null;
  /**
   * `?x=intel-rail-catalog`: what the gate shows goes through this, with whether the notice is confirmed: the children
   * once confirmed, otherwise what stands in for them (nothing while it reads, the error line, the notice form; null for a
   * person not on the named list). The catalog rail places it beside the host's own services, which are never gated.
   * Absent: drawn as is.
   */
  frame?: (gated: ReactNode | null, confirmed: boolean) => ReactNode;
}

type GateState = { kind: 'loading' } | { kind: 'error'; line: string } | { kind: 'ready'; notice: AckNotice };

export function AcknowledgementGate({ children, fetch: fetchImpl, token, frame }: AcknowledgementGateProps) {
  const [state, setState] = useState<GateState>({ kind: 'loading' });
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [line, setLine] = useState<string | null>(null);
  const alive = useRef(true);
  const uid = useId();
  const access = useCallback(() => (token ?? (() => authStorage.getAccessToken()))(), [token]);
  const doFetch = useCallback((i: string, n: RequestInit) => (fetchImpl ?? ((a: string, b: RequestInit) => fetch(a, b)))(i, n), [fetchImpl]);

  /** One GET, checked on arrival (A2); resolves to the gate state. Never sets state itself. */
  const fetchNotice = useCallback(async (): Promise<GateState> => {
    const tok = access();
    if (!tok) return { kind: 'error', line: SESSION_EXPIRED };
    try {
      const res = await doFetch(ACK_PATH, { method: 'GET', headers: { authorization: `Bearer ${tok}`, accept: 'application/json' } });
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }
      const notice = res.status === 200 ? parseAckNotice(body) : null;
      return notice ? { kind: 'ready', notice } : { kind: 'error', line: res.status === 401 ? SESSION_EXPIRED : STATE_LINE.technical! };
    } catch {
      return { kind: 'error', line: STATE_LINE.technical! };
    }
  }, [access, doFetch]);

  const read = useCallback(async () => {
    const next = await fetchNotice();
    if (alive.current) setState(next);
  }, [fetchNotice]);

  const reread = () => {
    setState({ kind: 'loading' });
    void read();
  };

  useEffect(() => {
    alive.current = true;
    void read();
    return () => {
      alive.current = false;
    };
  }, [read]);

  const confirm = async (notice: AckNotice) => {
    if (sending || !looksLikeFullName(name)) return;
    const tok = access();
    if (!tok) return setLine(SESSION_EXPIRED);
    setSending(true);
    setLine(null);
    try {
      const res = await doFetch(ACK_PATH, {
        method: 'POST',
        headers: { authorization: `Bearer ${tok}`, 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ confirmed: true, name: name.trim(), version: notice.version }),
      });
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }
      if (!alive.current) return;
      const ack = res.status === 201 || res.status === 200 ? ackOf(body) : undefined;
      if (ack && ack.version === notice.version) {
        setState({ kind: 'ready', notice: { ...notice, acknowledged: ack } });
      } else if (res.status === 409) {
        setLine(ACK_LINE.changed);
        void read();
      } else if (res.status === 400) {
        setLine(ACK_LINE.name);
      } else if (res.status === 403) {
        setState({ kind: 'ready', notice: { ...notice, listed: false } });
      } else {
        setLine(res.status === 401 ? SESSION_EXPIRED : ACK_LINE.notRecorded);
      }
    } catch {
      if (alive.current) setLine(ACK_LINE.notRecorded);
    } finally {
      if (alive.current) setSending(false);
    }
  };

  const show = (node: ReactNode | null) => (frame ? frame(node, false) : node);
  if (state.kind === 'loading') return show(null);
  if (state.kind === 'error') {
    return show(
      <div className={C.lines} data-slot="intelligence-ack-error">
        <p role="status" className={C.stateLine}>
          {state.line}
        </p>
        {state.line !== SESSION_EXPIRED ? (
          <Button variant="tertiary" size="sm" className="text-xs font-normal" onClick={reread}>
            Retry
          </Button>
        ) : null}
      </div>
    );
  }
  const n = state.notice;
  if (!n.listed) return show(null);
  if (n.acknowledged && n.acknowledged.version === n.version) return frame ? frame(children, true) : <>{children}</>;
  return show(
    <form
      className={C.card}
      data-slot="intelligence-ack"
      aria-labelledby={`${uid}-first`}
      onSubmit={(ev) => {
        ev.preventDefault();
        void confirm(n);
      }}
    >
      {n.lines.map((l, i) => (
        <p key={`${i}-${l}`} id={i === 0 ? `${uid}-first` : undefined} className={i === 0 ? C.title : N.readLine}>
          {l}
        </p>
      ))}
      <label htmlFor={`${uid}-name`} className={C.meta}>
        Full name
      </label>
      <TextField id={`${uid}-name`} autoComplete="off" value={name} onChange={(ev) => setName(ev.target.value)} />
      <Button type="submit" variant="secondary" size="sm" className="text-xs font-normal" disabled={sending || !looksLikeFullName(name)}>
        {n.confirm}
      </Button>
      {line ? (
        <p role="status" className={C.stateLine} data-slot="intelligence-ack-line">
          {line}
        </p>
      ) : null}
    </form>
  );
}
